/**
 * Food Service - API client for food database endpoints
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

/**
 * Get authentication headers
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

/**
 * List foods with pagination
 */
export const listFoods = async (options = {}) => {
  const { limit = 50, skip = 0, sort = 'description', order = 'asc' } = options;
  const params = new URLSearchParams({ limit, skip, sort, order });
  
  const response = await fetch(`${API_URL}/foods?${params}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to list foods');
  }
  
  return response.json();
};

/**
 * Search foods by text query
 */
export const searchFoods = async (query, options = {}) => {
  const { 
    limit = 50, 
    skip = 0,
    category,
    minProtein,
    maxProtein,
    minCalories,
    maxCalories,
    tags,
    dietaryTags, // Filter by dietary preferences (vegetarian, vegan, gluten-free, etc.)
    dataType // Filter by data type: 'branded', 'unbranded', or 'all'
  } = options;
  
  const params = new URLSearchParams({ q: query, limit, skip });
  
  if (category) params.append('category', category);
  if (minProtein) params.append('minProtein', minProtein);
  if (maxProtein) params.append('maxProtein', maxProtein);
  if (minCalories) params.append('minCalories', minCalories);
  if (maxCalories) params.append('maxCalories', maxCalories);
  if (tags) params.append('tags', Array.isArray(tags) ? tags.join(',') : tags);
  if (dietaryTags) params.append('dietaryTags', Array.isArray(dietaryTags) ? dietaryTags.join(',') : dietaryTags);
  if (dataType) params.append('dataType', dataType);
  
  const response = await fetch(`${API_URL}/foods/search?${params}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to search foods');
  }
  
  return response.json();
};

/**
 * Filter foods by macro ranges
 */
export const filterFoods = async (filters = {}, options = {}) => {
  const { limit = 50, skip = 0, sort = 'nutrients.protein', order = 'desc' } = options;
  
  const params = new URLSearchParams({ limit, skip, sort, order });
  
  // Add all filter parameters
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, Array.isArray(value) ? value.join(',') : value);
    }
  });
  
  const response = await fetch(`${API_URL}/foods/filter?${params}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to filter foods');
  }
  
  return response.json();
};

/**
 * Get all food categories
 */
export const getCategories = async () => {
  const response = await fetch(`${API_URL}/foods/categories`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to get categories');
  }
  
  return response.json();
};

/**
 * Get all food tags
 */
export const getTags = async () => {
  const response = await fetch(`${API_URL}/foods/tags`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to get tags');
  }
  
  return response.json();
};

/**
 * Get all dietary tags (vegetarian, vegan, gluten-free, etc.)
 */
export const getDietaryTags = async () => {
  const response = await fetch(`${API_URL}/foods/dietary-tags`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to get dietary tags');
  }
  
  return response.json();
};

/**
 * Get food database stats
 */
export const getStats = async () => {
  const response = await fetch(`${API_URL}/foods/stats`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to get stats');
  }
  
  return response.json();
};

/**
 * Get high-protein foods
 */
export const getHighProteinFoods = async (options = {}) => {
  const { limit = 20, minProtein = 20, maxCalories } = options;
  
  const params = new URLSearchParams({ limit, minProtein });
  if (maxCalories) params.append('maxCalories', maxCalories);
  
  const response = await fetch(`${API_URL}/foods/high-protein?${params}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to get high-protein foods');
  }
  
  return response.json();
};

/**
 * Find foods matching nutrition targets
 */
export const matchTargets = async (targets, options = {}) => {
  const { limit = 20, tolerance = 0.3 } = options;
  
  const params = new URLSearchParams({ limit, tolerance });
  
  if (targets.protein) params.append('targetProtein', targets.protein);
  if (targets.calories) params.append('targetCalories', targets.calories);
  if (targets.carbs) params.append('targetCarbs', targets.carbs);
  if (targets.fat) params.append('targetFat', targets.fat);
  
  const response = await fetch(`${API_URL}/foods/match-targets?${params}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to match targets');
  }
  
  return response.json();
};

/**
 * Get a single food by FDC ID
 */
export const getFood = async (fdcId) => {
  const response = await fetch(`${API_URL}/foods/${fdcId}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get food');
  }
  
  return response.json();
};

const foodService = {
  listFoods,
  searchFoods,
  filterFoods,
  getCategories,
  getTags,
  getDietaryTags,
  getStats,
  getHighProteinFoods,
  matchTargets,
  getFood
};

export default foodService;
